/**
 * Deriving what the device list shows.
 *
 * All the logic lives here, as pure functions, because this is where the bugs
 * would be and where exhaustive testing is cheap. The component that consumes it
 * is a list — and it gets rewritten the moment live view lands.
 *
 * The governing requirement: an offline device renders its last-seen time and
 * last-known camera state. Never an error screen. Once a device has been seen,
 * there is no path through this module that yields "something went wrong" — a
 * stale answer is always better than no answer, because "this camera was
 * streaming eleven minutes ago" is actionable and "Error" is not.
 */

import type { CameraState } from '@jeprotect/shared';

/**
 * How long after its last heartbeat a device is considered offline.
 *
 * Three missed heartbeats at 30s. One dropped heartbeat is normal on a domestic
 * uplink, and marking a device offline for a single lost packet would make the
 * fleet view useless. Mirrors DEVICE_OFFLINE_AFTER_MS on the cloud.
 */
export const DEVICE_OFFLINE_AFTER_MS = 90_000;

/** How often the list refreshes. One place, so Phase 5 changes it once. */
export const DEVICE_POLL_INTERVAL_MS = 15_000;

/** Beyond this, the data on screen is old enough to say so. */
export const STALE_AFTER_MS = 60_000;

export interface CameraSummary {
  id: string;
  name: string | null;
  state: CameraState;
  oldestSegmentAt: number | null;
  newestSegmentAt: number | null;
}

export interface DeviceSummary {
  id: string;
  name: string;
  siteName: string | null;
  /** Mirrors devices.status: unclaimed, claimed, online, offline, disabled. */
  status: string;
  /** Epoch ms, or null for a device that has never connected. */
  lastSeenAt: number | null;
  cameras: CameraSummary[];
}

/** What the last fetch did. Never surfaced as an error to the user. */
export type FetchState =
  | { status: 'loading' }
  | { status: 'ok'; fetchedAt: number }
  | { status: 'failed'; fetchedAt: number | null; lastSucceededAt: number | null };

export type DeviceHealth = 'online' | 'offline' | 'never-connected';

/**
 * What an operator can do with this device right now.
 *
 * Separate from health, because they answer different questions. Health is "is
 * it talking to us"; this is "has anyone finished installing it". A device can
 * be unclaimed and perfectly healthy, and an operator looking at a fleet needs
 * to tell those apart at a glance.
 */
export type ClaimState = 'unclaimed' | 'claimed';

export interface DeviceViewModel {
  id: string;
  name: string;
  siteName: string | null;
  health: DeviceHealth;
  claimState: ClaimState;
  /** Human phrase for the last contact, e.g. "11 minutes ago". */
  lastSeenLabel: string;
  lastSeenAt: number | null;
  cameras: CameraSummary[];
  cameraSummary: { streaming: number; total: number };
  /**
   * True when the data on screen is older than it should be — a failed refresh,
   * or simply a slow one. Renders as a marker beside the data, not instead of it.
   */
  stale: boolean;
  /** Present only when stale, explaining why in a phrase, never as an error. */
  staleReason: string | null;
}

/** Relative time, in units a person would use out loud. */
export function relativeTime(from: number, now: number): string {
  const deltaMs = Math.max(0, now - from);
  const seconds = Math.floor(deltaMs / 1000);

  if (seconds < 10) return 'just now';
  if (seconds < 60) return `${seconds} seconds ago`;

  const minutes = Math.floor(seconds / 60);
  if (minutes === 1) return '1 minute ago';
  if (minutes < 60) return `${minutes} minutes ago`;

  const hours = Math.floor(minutes / 60);
  if (hours === 1) return '1 hour ago';
  if (hours < 24) return `${hours} hours ago`;

  const days = Math.floor(hours / 24);
  return days === 1 ? '1 day ago' : `${days} days ago`;
}

function healthOf(lastSeenAt: number | null, now: number): DeviceHealth {
  if (lastSeenAt === null) return 'never-connected';
  return now - lastSeenAt <= DEVICE_OFFLINE_AFTER_MS ? 'online' : 'offline';
}

/**
 * Builds the view model for one device.
 *
 * Takes a failed fetch as an input rather than an exception. That is the whole
 * design: a failure downgrades confidence in the data, it does not replace the
 * data.
 */
export function deviceViewModel(
  device: DeviceSummary,
  fetch: FetchState,
  now: number,
): DeviceViewModel {
  const health = healthOf(device.lastSeenAt, now);
  // Anything other than the initial state counts as claimed: a disabled or
  // offline device has still been through commissioning, and offering to
  // generate a claim code for it would be wrong.
  const claimState: ClaimState = device.status === 'unclaimed' ? 'unclaimed' : 'claimed';

  const streaming = device.cameras.filter((camera) => camera.state === 'streaming').length;

  let stale = false;
  let staleReason: string | null = null;

  if (fetch.status === 'failed') {
    stale = true;
    staleReason =
      fetch.lastSucceededAt === null
        ? 'Not yet reached the server'
        : `Last updated ${relativeTime(fetch.lastSucceededAt, now)}`;
  } else if (fetch.status === 'ok' && now - fetch.fetchedAt > STALE_AFTER_MS) {
    stale = true;
    staleReason = `Last updated ${relativeTime(fetch.fetchedAt, now)}`;
  }

  return {
    id: device.id,
    name: device.name,
    siteName: device.siteName,
    health,
    claimState,
    lastSeenAt: device.lastSeenAt,
    lastSeenLabel:
      device.lastSeenAt === null ? 'Never connected' : relativeTime(device.lastSeenAt, now),
    // Always the last known cameras, whatever the fetch did.
    cameras: device.cameras,
    cameraSummary: { streaming, total: device.cameras.length },
    stale,
    staleReason,
  };
}

export interface DeviceListViewModel {
  devices: DeviceViewModel[];
  /** True only before the first successful load, when there is genuinely nothing. */
  showLoadingPlaceholder: boolean;
  /** True when a tenant has no devices at all. Distinct from a failed load. */
  showEmptyState: boolean;
  /** A quiet banner, never a full-page error. Null when everything is current. */
  connectionNotice: string | null;
  onlineCount: number;
}

/**
 * Builds the whole list.
 *
 * The distinction that matters is between "no data yet" and "no devices". A
 * failed first load is the former and must not render the empty state, or a
 * network blip reads as "you have no devices" — alarming and wrong.
 */
export function deviceListViewModel(
  devices: DeviceSummary[],
  fetch: FetchState,
  now: number,
): DeviceListViewModel {
  const models = devices.map((device) => deviceViewModel(device, fetch, now));

  const neverLoaded =
    fetch.status === 'loading' || (fetch.status === 'failed' && fetch.lastSucceededAt === null);

  let connectionNotice: string | null = null;
  if (fetch.status === 'failed') {
    connectionNotice =
      fetch.lastSucceededAt === null
        ? 'Cannot reach the server. Retrying.'
        : `Cannot reach the server. Showing data from ${relativeTime(fetch.lastSucceededAt, now)}.`;
  }

  return {
    devices: models,
    showLoadingPlaceholder: neverLoaded && devices.length === 0,
    // Only when a load has genuinely succeeded and returned nothing.
    showEmptyState: fetch.status === 'ok' && devices.length === 0,
    connectionNotice,
    onlineCount: models.filter((model) => model.health === 'online').length,
  };
}

/**
 * Whether to issue another poll.
 *
 * Paused when the tab is hidden: a background tab polling every 15s for hours
 * costs the operator's battery and the server's capacity for a screen nobody is
 * looking at. The next foreground render refreshes immediately, so nothing is
 * lost by stopping.
 */
export function shouldPoll(documentHidden: boolean): boolean {
  return !documentHidden;
}
